
import React, { useState } from 'react';
import { ModalCampaign } from '@/types/modalCampaigns';
import { 
  Table, 
  TableHeader, 
  TableRow, 
  TableHead, 
  TableBody, 
  TableCell 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { BarChart, Edit, Plus, RefreshCw, Trash } from 'lucide-react';
import { formatDate } from '@/utils/formatDate';
import { deleteCampaign } from '@/services/modalCampaigns';
import { Badge } from '@/components/ui/badge';

interface CampaignListProps {
  campaigns: ModalCampaign[];
  isLoading: boolean;
  onEdit: (campaign: ModalCampaign) => void;
  onRefresh: () => void;
  onCreateNew: () => void;
  onViewStats: (campaign: ModalCampaign) => void;
}

const CampaignList: React.FC<CampaignListProps> = ({
  campaigns,
  isLoading,
  onEdit,
  onRefresh,
  onCreateNew,
  onViewStats
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<ModalCampaign | null>(null);

  const handleDelete = async () => {
    if (!campaignToDelete) return;
    
    try {
      await deleteCampaign(campaignToDelete.id);
      toast.success('Campagne succesvol verwijderd');
      onRefresh();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Er is een fout opgetreden bij het verwijderen van de campagne');
    } finally {
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);
    }
  };

  const confirmDelete = (campaign: ModalCampaign) => {
    setCampaignToDelete(campaign);
    setDeleteDialogOpen(true);
  };

  // Function to check if a campaign is active
  const isCampaignActive = (campaign: ModalCampaign) => {
    const now = new Date();
    const startDate = new Date(campaign.start_date);
    const endDate = new Date(campaign.end_date);
    return now >= startDate && now <= endDate;
  };

  // Function to get status badge for a campaign
  const getCampaignStatusBadge = (campaign: ModalCampaign) => {
    const now = new Date();
    const startDate = new Date(campaign.start_date);
    const endDate = new Date(campaign.end_date);

    if (now < startDate) {
      return <Badge variant="secondary">Toekomstig</Badge>;
    } else if (now > endDate) {
      return <Badge variant="outline">Verlopen</Badge>;
    } else {
      return <Badge variant="default" className="bg-green-500">Actief</Badge>;
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Modal Campagnes</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isLoading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Vernieuwen
          </Button>
          <Button size="sm" onClick={onCreateNew}>
            <Plus className="h-4 w-4 mr-2" />
            Nieuwe campagne
          </Button>
        </div>
      </div>
      
      {isLoading ? (
        <div className="py-12 flex justify-center items-center">
          <p className="text-muted-foreground">Campagnes laden...</p>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-muted-foreground mb-4">Geen campagnes gevonden</p>
          <Button onClick={onCreateNew}>
            <Plus className="h-4 w-4 mr-2" />
            Eerste campagne aanmaken
          </Button>
        </div>
      ) : (
        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Titel</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Periode</TableHead>
                <TableHead>Frequentie</TableHead>
                <TableHead>Quiz</TableHead>
                <TableHead>Aangemaakt</TableHead>
                <TableHead>Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell className="font-medium">{campaign.title}</TableCell>
                  <TableCell>{getCampaignStatusBadge(campaign)}</TableCell>
                  <TableCell>
                    <div className="text-xs">
                      <div>Start: {formatDate(campaign.start_date)}</div>
                      <div>Eind: {formatDate(campaign.end_date)}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {campaign.frequency === 'once' && 'Eenmalig'}
                    {campaign.frequency === 'every_login' && 'Bij iedere inlog'}
                    {campaign.frequency === 'every_minute' && 'Elke minuut'}
                    {campaign.frequency === 'hourly' && 'Elk uur'}
                    {campaign.frequency === 'daily' && 'Dagelijks'}
                    {campaign.frequency === 'weekly' && 'Wekelijks'}
                    {campaign.frequency === 'monthly' && 'Maandelijks'}
                  </TableCell>
                  <TableCell>
                    {campaign.has_quiz ? (
                      <Badge variant="secondary">Quiz</Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">Geen</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(campaign.created_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => onViewStats(campaign)} title="Statistieken">
                        <BarChart className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => onEdit(campaign)} title="Bewerken">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => confirmDelete(campaign)} title="Verwijderen">
                        <Trash className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Campagne verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze campagne wilt verwijderen?
              "{campaignToDelete?.title}"
              <br /><br />
              Dit kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CampaignList;
